<script setup lang="ts">
import { analyticsModule } from '@/store/analytics/module'
import { bundleModule } from '@/store/bundle/module'
import { examMetadataModule } from '@/store/examMetadata/module'
import { mobileModule } from '@/store/mobile/module'
import { userModule } from '@/store/user/module'
import UIKit from '@pocketprep/ui-kit'
import { computed } from 'vue'

const {
    ModalContainer,
    Modal,
    Button: PocketButton,
} = UIKit

const clientVersion = import.meta.env.VUE_APP_VERSION

const isModalVisible = computed(() => {
    return mobileModule.getters.getShowReturnToMobileModal()
})

const isDarkMode = computed(() => {
    return userModule.state.settings.isDarkMode
})

const currentExamMetadata = computed(() => {
    return examMetadataModule.getters.getCurrentExamMetadata()
})

const examMetadataById = computed(() => {
    return examMetadataModule.getters.getExamMetadataById()

})

const bundle = computed(() => {
    return bundleModule.getters.getBundles()
        .find(b => b.exams.find(e => {
            const bundleExam = examMetadataById.value[e.objectId]
            return currentExamMetadata.value && bundleExam?.examGuid === currentExamMetadata.value.examGuid
        }))
})

const returnToMobile = () => {
    mobileModule.actions.returnToMobileApp()

    analyticsModule.actions.amplitudeTrack('Button Clicked', {
        client: 'Web Study',
        clientAppId: 'study.pocketprep.com',
        clientVersion: clientVersion,
        callToAction: 'Open App',
        containerName: 'Study',
        screenName: 'Back to Mobile Modal',
        examGuid: currentExamMetadata.value?.examGuid || '',
        bundleId: bundle.value?.objectId || '',
    })
}

const stayInBrowser = () => {
    mobileModule.actions.closeReturnToMobileModal()

    analyticsModule.actions.amplitudeTrack('Button Clicked', {
        client: 'Web Study',
        clientAppId: 'study.pocketprep.com',
        clientVersion: clientVersion,
        callToAction: 'Stay in Browser',
        containerName: 'Study',
        screenName: 'Back to Mobile Modal',
        examGuid: currentExamMetadata.value?.examGuid || '',
        bundleId: bundle.value?.objectId || '',
    })
}

</script>

<template>
    <ModalContainer v-if="isModalVisible">
        <template #modal>
            <Modal
                class="return-to-app__modal"
                :is-dark-mode="isDarkMode"
                :show-close-button="true"
                @close="stayInBrowser"
            >
                <div class="return-to-app">
                    <div
                        v-dark
                        ref="return-to-app__title"
                        class="return-to-app__title"
                        tabindex="-1"
                    >
                        Back to App?
                    </div>
                    <div
                        v-dark
                        class="return-to-app__text"
                    >
                        Thanks for purchasing premium. You can study on the web or in the app.
                    </div>
                </div>
                <div class="return-to-app__actions">
                    <PocketButton
                        class="return-to-app__action-btn"
                        :is-dark-mode="isDarkMode"
                        type="secondary"
                        @click="stayInBrowser"
                    >
                        Stay in Browser
                    </PocketButton>
                    <PocketButton
                        class="return-to-app__action-btn"
                        :is-dark-mode="isDarkMode"
                        @click="returnToMobile"
                    >
                        Open App
                    </PocketButton>
                </div>
            </Modal>
        </template>
    </ModalContainer>
</template>

<style lang="scss" scoped>
.return-to-app {
    text-align: center;
    padding: 0 40px;

    &__modal {
        left: 50%;
        position: absolute;
        transform: translateX(-50%);
        top: 80px;
    
        @include breakpoint(black-bear) {
            width: 356px;
            height: auto;
            max-height: 638px;
            top: 50%;
            transform: translate(-50%, -50%);
        }
    }

    &__actions {
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 32px 0 24px;
        gap: 8px;
    }
    
    &__title {
        margin-top: 36px;
        margin-bottom: 12px;
        font-weight: 600;
        font-size: 18px;
        letter-spacing: 0.3px;
        line-height: 25px;

        &--dark {
            color: $fog
        }
    }

    &__text {
        color: $ash;
        font-size: 15px;
        line-height: 20px;

        &--dark {
            color: $fog;
        }
    }
}
</style>